/**
 * Client-side interface for the physics (gravity / cloth) Web Worker.
 *
 * Mirrors `inkingWorkerClient.ts`: a singleton worker, an abortable
 * promise-based call, and per-substep progress streaming. The store
 * dispatches `simulateGravity` through this so the main thread never
 * blocks on the PBD solver.
 */

import { createSingletonWorker } from "../workers/createWorkerRPC.ts";
import type {
  PhysicsProgressEvent,
  PhysicsWorkerRequest,
  PhysicsWorkerResponse,
} from "./physics.worker.ts";
import {
  packedSettleTransferables,
  packSettleInput,
  type SettleInput,
  type SettleOutput,
  unpackSettleResult,
} from "./physicsSim.ts";

const getWorker = createSingletonWorker(
  () =>
    new Worker(new URL("./physics.worker.ts", import.meta.url), {
      type: "module",
    }),
);

let _nextId = 1;

export interface PhysicsSimProgress {
  /** Solver substep just completed. */
  substep: number;
  /** Solver max-substep budget — denominator for a determinate bar. */
  total: number;
  /** Wallclock since the worker received the request, in ms. */
  elapsedMs: number;
}

export interface SettleParticlesAsyncOptions {
  /** Cancel the solve cooperatively at the next substep boundary. */
  signal?: AbortSignal;
  /** Streamed every `progressStride` substeps (default ~4). */
  onProgress?: (ev: PhysicsSimProgress) => void;
  /** How often, in substeps, the worker should emit a progress event.
   *  Defaults to 4 — small enough for a smooth bar, large enough that
   *  postMessage overhead stays negligible. */
  progressStride?: number;
  /** First substep at which the trajectory recorder begins capturing.
   *  `0` (default) = include the initial pose as frame 0; > 0 shifts the
   *  bake window forward (used by the menu's opt-in time-range bake). */
  recordStartSubstep?: number;
  /** Stall warning threshold (ms). Shows a toast when exceeded. */
  stallWarningMs?: number;
  /** Custom stall warning message. */
  stallMessage?: string;
}

/** Custom error thrown when the solve was cancelled via `options.signal`.
 *  Lets callers distinguish expected cancellations from real failures. */
export class GravitySimCancelledError extends Error {
  /** Whatever frames the solver had captured before the cancel boundary.
   *  Useful when the caller wants to keep the partial scrub even though
   *  the user changed their mind. Pass through `discardPartialResult` in
   *  the options below to suppress it. */
  partial?: SettleOutput;
  constructor(partial?: SettleOutput) {
    super("Gravity simulation cancelled");
    this.name = "GravitySimCancelledError";
    this.partial = partial;
  }
}

/**
 * Run the PBD solver in the physics Web Worker.
 *
 * The caller's positions / edges / pinned / etc. typed-array buffers are
 * TRANSFERRED to the worker (zero-copy). Reading them on the main thread
 * after this call is a programmer error — use the returned
 * `SettleOutput.positions` instead.
 */
export async function settleParticlesAsync(
  input: SettleInput,
  options: SettleParticlesAsyncOptions = {},
): Promise<SettleOutput> {
  const worker = getWorker();
  const rpcId = _nextId++;

  // Pack on the main thread — THREE.Mesh handles can't cross the worker
  // boundary, so the triangle soup must be assembled here first.
  const request = packSettleInput(input, options.recordStartSubstep ?? 0);
  const transferables = packedSettleTransferables(request);
  const progressStride = Math.max(1, Math.floor(options.progressStride ?? 4));

  const message: PhysicsWorkerRequest = {
    kind: "run",
    _rpcId: rpcId,
    request,
    progressStride,
  };

  return new Promise<SettleOutput>((resolve, reject) => {
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let signalListener: (() => void) | null = null;

    const cleanup = () => {
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
      if (signalListener && options.signal) {
        options.signal.removeEventListener("abort", signalListener);
        signalListener = null;
      }
    };

    const onMessage = (e: MessageEvent<PhysicsWorkerResponse>) => {
      const m = e.data;
      if (!m || m._rpcId !== rpcId) return;
      if (m.kind === "progress") {
        options.onProgress?.(toProgress(m));
        return;
      }
      if (m.kind === "cancelled") {
        cleanup();
        reject(new GravitySimCancelledError(unpackSettleResult(m.result)));
        return;
      }
      if (m.kind === "done") {
        cleanup();
        if (m.ok) resolve(unpackSettleResult(m.result));
        else reject(new Error(m.error ?? "Worker error"));
        return;
      }
    };

    const onError = (e: ErrorEvent) => {
      cleanup();
      reject(new Error(e.message ?? "Worker error"));
    };

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);

    if (options.stallWarningMs && options.stallWarningMs > 0) {
      const stallMessage =
        options.stallMessage ??
        "Gravity simulation is taking longer than expected.";
      stallTimer = setTimeout(() => {
        import("../ui/toastBus.ts")
          .then(({ showToast }) => {
            showToast(stallMessage, {
              severity: "warning",
              duration: 6000,
              dedupKey: "gravity-sim",
            });
          })
          .catch(() => {});
      }, options.stallWarningMs);
    }

    if (options.signal) {
      if (options.signal.aborted) {
        worker.postMessage({
          kind: "cancel",
          _rpcId: rpcId,
        } satisfies PhysicsWorkerRequest);
      } else {
        signalListener = () => {
          worker.postMessage({
            kind: "cancel",
            _rpcId: rpcId,
          } satisfies PhysicsWorkerRequest);
        };
        options.signal.addEventListener("abort", signalListener);
      }
    }

    worker.postMessage(message, transferables);
  });
}

function toProgress(ev: PhysicsProgressEvent): PhysicsSimProgress {
  return { substep: ev.substep, total: ev.total, elapsedMs: ev.elapsedMs };
}
